import apiClient from './api';

export interface Developer {
  id: number;
  name: string;
  email: string;
  active_projects_count: number;
  active_projects: Array<{ id: number; name: string; status: string }>;
  availability: 'available' | 'limited' | 'busy';
}

export interface PendingAssignment {
  id: number;
  name: string;
  description: string;
  client: { id: number; name: string; email: string };
  status: string;
  assigned_at: string;
  estimated_hours: number;
  tech_stack: string[];
}

class ProjectAssignmentService {
  /**
   * Get available developers for assignment
   */
  async getAvailableDevelopers(): Promise<Developer[]> {
    const response = await apiClient.get('/admin/assignments/available-developers');
    return response.data.data;
  }

  /**
   * Assign developers to a project
   */
  async assignDevelopers(
    projectId: number,
    developerIds: number[],
    notify: boolean = true
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/admin/assignments/assign', {
      project_id: projectId,
      developer_ids: developerIds,
      notify,
    });
    return response.data;
  }

  /**
   * Remove developer from project
   */
  async removeDeveloper(
    projectId: number,
    developerId: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete('/admin/assignments/remove', {
      data: { project_id: projectId, developer_id: developerId },
    });
    return response.data;
  }

  /**
   * Get pending assignments for developer
   */
  async getPendingAssignments(): Promise<PendingAssignment[]> {
    const response = await apiClient.get('/developer/assignments/pending');
    return response.data.data;
  }

  /**
   * Accept project assignment
   */
  async acceptAssignment(projectId: number): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`/developer/assignments/${projectId}/accept`);
    return response.data;
  }

  /**
   * Decline project assignment
   */
  async declineAssignment(
    projectId: number,
    reason?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`/developer/assignments/${projectId}/decline`, {
      reason,
    });
    return response.data;
  }
}

export default new ProjectAssignmentService();
